import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useApi, errMsg, notifyRefresh } from "@/lib/api";
import { fmtDate } from "@/lib/constants";

const CHECKS = [
  ["hipertensao", "Hipertensão"], ["diabetes", "Diabetes"], ["cardiaco", "Problemas cardíacos"],
  ["alergia", "Alergia a medicamentos"], ["anticoagulante", "Usa anticoagulante"], ["gestante", "Gestante"],
  ["hemorragia", "Histórico de hemorragia"], ["fumante", "Fumante"], ["hepatite", "Hepatite / doença infecciosa"],
];
const TEXTS = [
  ["queixa_principal", "Queixa principal"], ["medicamentos", "Medicamentos em uso"],
  ["cirurgias", "Cirurgias / internações anteriores"], ["observacoes", "Observações clínicas"],
];

export const AnamneseTab = ({ p }) => {
  const api = useApi();
  const [f, setF] = useState(p.anamnese || {});
  const [saving, setSaving] = useState(false);
  useEffect(() => { setF(p.anamnese || {}); }, [p.anamnese]);

  const alerts = CHECKS.filter(([k]) => f[k]).map(([, l]) => l);
  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/patients/${p.id}/anamnese`, f);
      toast.success("Anamnese salva");
      notifyRefresh();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="surface p-5 sm:p-6" data-testid="anamnese-form">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-display text-lg font-bold text-slate-900">Ficha de anamnese</h3>
          <p className="text-xs text-slate-500">{p.anamnese_updated_at ? `Atualizada em ${fmtDate(p.anamnese_updated_at)}` : "Preencha na primeira consulta"}</p>
        </div>
        <Button onClick={save} disabled={saving} className="rounded-full bg-sky-600 hover:bg-sky-700" data-testid="anamnese-save-btn">
          {saving ? "Salvando..." : "Salvar anamnese"}
        </Button>
      </div>
      {alerts.length > 0 && (
        <div className="mt-4 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800" data-testid="anamnese-alerts">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span><b>Atenção:</b> {alerts.join(" · ")}{f.alergia && f.alergia_desc ? ` (${f.alergia_desc})` : ""}</span>
        </div>
      )}
      <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {CHECKS.map(([k, l]) => (
          <label key={k} className={`flex cursor-pointer items-center gap-3 rounded-xl border px-3 py-2.5 text-sm transition-colors ${f[k] ? "border-amber-300 bg-amber-50/60" : "border-slate-200 hover:border-sky-200"}`}>
            <Checkbox checked={!!f[k]} onCheckedChange={(v) => setF({ ...f, [k]: !!v })} data-testid={`anamnese-${k}-checkbox`} />
            {l}
          </label>
        ))}
      </div>
      {f.alergia && (
        <div className="mt-3 grid gap-1.5">
          <Label>Quais alergias?</Label>
          <Input value={f.alergia_desc || ""} onChange={(e) => setF({ ...f, alergia_desc: e.target.value })} data-testid="anamnese-alergia-desc-input" />
        </div>
      )}
      <div className="mt-5 grid gap-4 sm:grid-cols-2">
        {TEXTS.map(([k, l]) => (
          <div key={k} className="grid gap-1.5">
            <Label>{l}</Label>
            <Textarea rows={3} value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`anamnese-${k}-input`} />
          </div>
        ))}
      </div>
    </div>
  );
};
